import { useState, useEffect, useRef } from 'react';

/**
 * Justified row layout — all photos share the same height (targetRowHeight).
 * Each full row distributes leftover space proportionally among its photos
 * so the row fills edge-to-edge. The last (incomplete) row is left-aligned.
 *
 * Row-break criterion: natural widths at targetRowHeight would exceed the
 * container width.
 *
 * @param {Array}  photos          - array of { src, alt, width, height, ... }
 * @param {number} targetRowHeight - fixed height for every photo (default 320)
 * @param {number} gap             - px gap between photos (default 8)
 */
export function useJustifiedLayout(photos, { targetRowHeight = 320, gap = 8 } = {}) {
  const containerRef = useRef(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!containerRef.current || photos.length === 0) return;

    function naturalWidth(p) {
      return (p.width / p.height) * targetRowHeight;
    }

    function buildRow(group, containerWidth, isLast) {
      const gapTotal = (group.length - 1) * gap;
      const naturalTotal = group.reduce((sum, p) => sum + naturalWidth(p), 0);
      const leftover = containerWidth - gapTotal - naturalTotal;

      return group.map(p => {
        const nw = naturalWidth(p);
        // Distribute leftover proportionally to each photo's natural width
        const extra = isLast ? 0 : leftover * (nw / naturalTotal);
        return {
          ...p,
          computedWidth: nw + extra,
          computedHeight: targetRowHeight,
          isLastRow: isLast,
        };
      });
    }

    function compute(containerWidth) {
      const result = [];
      let currentRow = [];
      let rowNaturalWidth = 0;

      for (let i = 0; i < photos.length; i++) {
        const nw = naturalWidth(photos[i]);
        const gapIfAdded = currentRow.length * gap;

        if (currentRow.length > 0 && rowNaturalWidth + gapIfAdded + nw > containerWidth) {
          result.push(buildRow(currentRow, containerWidth, false));
          currentRow = [];
          rowNaturalWidth = 0;
        }

        currentRow.push(photos[i]);
        rowNaturalWidth += nw;
      }

      if (currentRow.length > 0) {
        result.push(buildRow(currentRow, containerWidth, true));
      }

      setRows(result);
    }

    const ro = new ResizeObserver(entries => {
      compute(entries[0].contentRect.width);
    });
    ro.observe(containerRef.current);
    compute(containerRef.current.getBoundingClientRect().width);

    return () => ro.disconnect();
  }, [photos, targetRowHeight, gap]);

  return { containerRef, rows };
}
